import { Fragment, type ReactNode } from "react";

// WHY: We avoid pulling react-markdown (~50KB + remark-gfm) for the small set of
// constructs we actually need. Hand-rolling lets us guarantee URL scheme allow-list
// (http/https/mailto only) and produce React nodes — no dangerouslySetInnerHTML.

type InlineToken =
  | { kind: "text"; value: string }
  | { kind: "code"; value: string }
  | { kind: "strong"; value: string }
  | { kind: "em"; value: string }
  | { kind: "link"; label: string; url: string };

const URL_RE = /^(https?:|mailto:)/i;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UL_RE = /^[-*]\s+/;
const OL_RE = /^\d+\.\s+/;

function safeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (URL_RE.test(trimmed)) return trimmed;
  return null;
}

function tokenizeInline(src: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let i = 0;
  let buf = "";

  const flushText = () => {
    if (buf.length > 0) {
      tokens.push({ kind: "text", value: buf });
      buf = "";
    }
  };

  while (i < src.length) {
    const ch = src[i];

    if (ch === "`") {
      const end = src.indexOf("`", i + 1);
      if (end !== -1) {
        flushText();
        tokens.push({ kind: "code", value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === "*" && src[i + 1] === "*") {
      const end = src.indexOf("**", i + 2);
      if (end !== -1) {
        flushText();
        tokens.push({ kind: "strong", value: src.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    if (ch === "*") {
      const end = src.indexOf("*", i + 1);
      if (end !== -1 && end > i + 1) {
        flushText();
        tokens.push({ kind: "em", value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === "[") {
      const labelEnd = src.indexOf("]", i + 1);
      if (labelEnd !== -1 && src[labelEnd + 1] === "(") {
        const urlEnd = src.indexOf(")", labelEnd + 2);
        if (urlEnd !== -1) {
          const url = safeUrl(src.slice(labelEnd + 2, urlEnd));
          if (url) {
            flushText();
            tokens.push({ kind: "link", label: src.slice(i + 1, labelEnd), url });
            i = urlEnd + 1;
            continue;
          }
        }
      }
    }

    buf += ch;
    i += 1;
  }
  flushText();
  return tokens;
}

function renderInline(src: string): ReactNode {
  const tokens = tokenizeInline(src);
  return tokens.map((t, idx) => {
    switch (t.kind) {
      case "code":
        return (
          <code
            key={idx}
            style={{
              background: "#f1f5f9",
              padding: "0 4px",
              borderRadius: 3,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.92em",
            }}
          >
            {t.value}
          </code>
        );
      case "strong":
        return <strong key={idx}>{t.value}</strong>;
      case "em":
        return <em key={idx}>{t.value}</em>;
      case "link":
        return (
          <a key={idx} href={t.url} target="_blank" rel="noopener noreferrer">
            {t.label}
          </a>
        );
      case "text":
      default:
        return <Fragment key={idx}>{t.value}</Fragment>;
    }
  });
}

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "code"; value: string }
  | { kind: "p"; text: string }
  | { kind: "blank" };

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      blocks.push({ kind: "blank" });
      i += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const collected: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith("```")) {
        collected.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      blocks.push({ kind: "code", value: collected.join("\n") });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (UL_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        items.push(lines[i].replace(UL_RE, ""));
        i += 1;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }

    if (OL_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && OL_RE.test(lines[i])) {
        items.push(lines[i].replace(OL_RE, ""));
        i += 1;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }

    const paraLines: string[] = [line];
    i += 1;
    while (
      i < lines.length
      && lines[i].trim() !== ""
      && !lines[i].startsWith("```")
      && !HEADING_RE.test(lines[i])
      && !UL_RE.test(lines[i])
      && !OL_RE.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push({ kind: "p", text: paraLines.join("\n") });
  }

  return blocks;
}

interface Props {
  source: string;
  style?: React.CSSProperties;
}

export function Markdown({ source, style }: Props) {
  const blocks = parseBlocks(source);
  return (
    <div style={{ lineHeight: 1.5, ...style }}>
      {blocks.map((b, idx) => {
        switch (b.kind) {
          case "heading": {
            const fontSize = Math.max(13, 22 - (b.level - 1) * 2);
            return (
              <div
                key={idx}
                style={{ fontSize, fontWeight: 600, margin: "10px 0 4px" }}
              >
                {renderInline(b.text)}
              </div>
            );
          }
          case "ul":
            return (
              <ul key={idx} style={{ margin: "4px 0", paddingLeft: 20 }}>
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={idx} style={{ margin: "4px 0", paddingLeft: 20 }}>
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            );
          case "code":
            return (
              <pre
                key={idx}
                style={{
                  background: "#0f172a",
                  color: "#f1f5f9",
                  padding: "8px 10px",
                  borderRadius: 4,
                  fontSize: 12,
                  overflowX: "auto",
                  margin: "6px 0",
                }}
              >
                <code>{b.value}</code>
              </pre>
            );
          case "p":
            return (
              <p key={idx} style={{ margin: "6px 0", whiteSpace: "pre-wrap" }}>
                {renderInline(b.text)}
              </p>
            );
          case "blank":
            return null;
        }
      })}
    </div>
  );
}

interface MetaLink {
  url: string;
  label?: string;
}

export function MetaLinks({ links }: { links: MetaLink[] }) {
  const filtered: { url: string; label?: string }[] = [];
  for (const l of links) {
    const safe = safeUrl(l.url);
    if (safe !== null) filtered.push({ url: safe, label: l.label });
  }
  if (filtered.length === 0) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2, textTransform: "uppercase" }}>
        Links
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {filtered.map((l, i) => (
          <li key={i}>
            <a href={l.url} target="_blank" rel="noopener noreferrer">
              {l.label || l.url}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
